/**
 * HTTP client. Folosește credentials: 'include' pentru a trimite cookie-ul
 * HttpOnly de sesiune automat.
 */

import i18n from '@/i18n';

const API_URL = import.meta.env.VITE_API_URL || '/api';

/** Peste atât, cererea e abandonată — altfel un server blocat ține UI-ul în „Se încarcă". */
const REQUEST_TIMEOUT_MS = 60_000;

export class ApiError extends Error {
  constructor(
    public status: number,
    public detail: string,
    public raw?: unknown,
  ) {
    super(`[${status}] ${detail}`);
  }
}

/** Mesajul de afișat pentru o eroare prinsă: `detail` de la server, altfel mesajul ei. */
export function errorMessage(err: unknown, fallback?: string): string {
  if (err instanceof ApiError) return err.detail;
  if (err instanceof Error) return err.message;
  return fallback ?? i18n.t('common.error');
}

/**
 * Hook setat de auth store pentru a reacționa la 401 pe endpoints non-auth.
 * Evităm import direct (ar face ciclu). La 401, dacă sesiunea a expirat pe
 * server, curățăm state-ul local și redirecționăm la /login.
 */
let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: () => void): void {
  onUnauthorized = fn;
}

function isAuthEndpoint(path: string): boolean {
  // Endpoints care primesc 401 „normal" (credențiale greșite, etc.)
  return (
    path.startsWith('/auth/login') ||
    path.startsWith('/auth/register') ||
    path.startsWith('/auth/salt') ||
    path.startsWith('/auth/recovery/params') ||
    path.startsWith('/auth/recovery/start') ||
    path.startsWith('/auth/recovery/complete') ||
    path.startsWith('/auth/logout')
  );
}

/**
 * FastAPI întoarce `detail` ca string la HTTPException, dar ca listă de
 * `{loc, msg}` la 422 (validare Pydantic) — `String()` ar da `[object Object]`.
 */
function formatDetail(detail: unknown): string {
  if (!Array.isArray(detail)) return String(detail);
  return detail
    .map((item) => {
      if (typeof item !== 'object' || item === null) return String(item);
      const { loc, msg } = item as { loc?: unknown[]; msg?: unknown };
      const field = Array.isArray(loc) ? loc.filter((p) => p !== 'body').join('.') : '';
      return field ? `${field}: ${String(msg)}` : String(msg);
    })
    .join('; ');
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  signal?: AbortSignal,
): Promise<T> {
  // Timeout propriu, legat de semnalul apelantului (TanStack Query anulează
  // cererile la unmount).
  const controller = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, REQUEST_TIMEOUT_MS);
  const onCallerAbort = () => controller.abort();
  if (signal?.aborted) controller.abort();
  else signal?.addEventListener('abort', onCallerAbort, { once: true });

  let res: Response;
  let text: string;
  try {
    res = await fetch(`${API_URL}${path}`, {
      method,
      credentials: 'include',
      headers: body ? { 'Content-Type': 'application/json' } : undefined,
      body: body ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    });
    text = await res.text();
  } catch (err) {
    if (timedOut) throw new ApiError(0, i18n.t('common.errors.timeout'));
    throw err;
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', onCallerAbort);
  }

  const data = text ? (() => { try { return JSON.parse(text); } catch { return text; } })() : null;

  if (!res.ok) {
    let detail = typeof data === 'object' && data && 'detail' in data
      ? formatDetail((data as { detail: unknown }).detail)
      : res.statusText;
    // Serverul trimite și un cod stabil; dacă îl cunoaștem, arătăm mesajul
    // tradus în locul textului brut (în română) din `detail`.
    const code = typeof data === 'object' && data ? (data as { code?: unknown }).code : undefined;
    if (typeof code === 'string' && i18n.exists(`api_errors.${code}`)) {
      detail = i18n.t(`api_errors.${code}`);
    }
    if (res.status === 429) {
      // Limita poate veni din backend (JSON) sau din Nginx (HTML) — mesaj unic.
      const seconds = Number(res.headers.get('Retry-After'));
      const message =
        Number.isFinite(seconds) && seconds > 0
          ? i18n.t('common.errors.rate_limited_in', { count: Math.ceil(seconds) })
          : i18n.t('common.errors.rate_limited');
      throw new ApiError(429, message, data);
    }
    if (res.status === 401 && !isAuthEndpoint(path) && onUnauthorized) {
      // Server consideră sesiunea expirată/invalidă → curățăm blob-ul
      // client-side (sessionStorage + keys în memorie).
      onUnauthorized();
    }
    throw new ApiError(res.status, detail, data);
  }
  return data as T;
}

export const api = {
  get:    <T = unknown>(p: string, s?: AbortSignal) => request<T>('GET', p, undefined, s),
  post:   <T = unknown>(p: string, b?: unknown, s?: AbortSignal) => request<T>('POST', p, b, s),
  put:    <T = unknown>(p: string, b?: unknown, s?: AbortSignal) => request<T>('PUT', p, b, s),
  patch:  <T = unknown>(p: string, b?: unknown, s?: AbortSignal) => request<T>('PATCH', p, b, s),
  delete: <T = unknown>(p: string, s?: AbortSignal) => request<T>('DELETE', p, undefined, s),
};

export async function getHealth(): Promise<{ status: string; db: string; redis: string }> {
  return api.get('/health');
}
